import React from 'react';
import { Eye } from 'lucide-react';
import UnitCard from './UnitCard';

interface UnitsGridProps {
  units: any[];
  viewMode: 'grid' | 'table';
  onUnitClick: (unit: any) => void;
}

const UnitsGrid: React.FC<UnitsGridProps> = ({ units, viewMode, onUnitClick }) => {
  if (units.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          Aucun lot ne correspond à vos critères de recherche
        </p>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {units.map((unit) => (
          <UnitCard key={unit.id} unit={unit} onClick={() => onUnitClick(unit)} />
        ))}
      </div>
    );
  }

  // Vue tableau
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                N° Lot
              </th>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                Bloc
              </th>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                Type
              </th>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                Étage
              </th>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                Surface
              </th>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                Prix
              </th>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                Statut
              </th>
              <th className="px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {units.map((unit) => (
              <tr
                key={unit.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={() => onUnitClick(unit)}
              >
                <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                  {unit.unit_number}
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {unit.block_name}
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 uppercase">
                  {unit.unit_type}
                  {unit.is_duplex && ' (Duplex)'}
                  {unit.is_triplex && ' (Triplex)'}
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {unit.floor === 0 ? 'RDC' : `Étage ${unit.floor}`}
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {Math.round(unit.area)} m²
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                  {new Intl.NumberFormat('fr-DZ', { maximumFractionDigits: 0 }).format(unit.final_price)} DA
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    unit.status === 'available' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                    unit.status === 'reserved' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                    unit.status === 'sold' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {unit.status === 'available' ? 'Disponible' :
                     unit.status === 'reserved' ? 'Réservé' :
                     unit.status === 'sold' ? 'Vendu' : 'Indisponible'}
                  </span>
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3">
                  <button className="text-green-600 hover:text-green-700 p-2 sm:p-1">
                    <Eye className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UnitsGrid;
